import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "jF-inline-flex jF-items-center jF-justify-center jF-gap-2 jF-whitespace-nowrap jF-rounded-md jF-text-sm jF-font-medium jF-ring-offset-white jF-transition-colors focus-visible:jF-outline-none focus-visible:jF-ring-2 focus-visible:jF-ring-slate-950 focus-visible:jF-ring-offset-2 disabled:jF-pointer-events-none disabled:jF-opacity-50 [&_svg]:jF-pointer-events-none [&_svg]:jF-size-4 [&_svg]:jF-shrink-0 dark:jF-ring-offset-slate-950 dark:focus-visible:jF-ring-slate-300",
  {
    variants: {
      variant: {
        default: "jF-bg-slate-900 jF-text-slate-50 hover:jF-bg-slate-900/90 dark:jF-bg-slate-50 dark:jF-text-slate-900 dark:hover:jF-bg-slate-50/90",
        destructive:
          "jF-bg-red-500 jF-text-slate-50 hover:jF-bg-red-500/90 dark:jF-bg-red-900 dark:jF-text-slate-50 dark:hover:jF-bg-red-900/90",
        outline:
          "jF-border jF-border-slate-200 jF-bg-white hover:jF-bg-slate-100 hover:jF-text-slate-900 dark:jF-border-slate-800 dark:jF-bg-slate-950 dark:hover:jF-bg-slate-800 dark:hover:jF-text-slate-50",
        secondary:
          "jF-bg-slate-100 jF-text-slate-900 hover:jF-bg-slate-100/80 dark:jF-bg-slate-800 dark:jF-text-slate-50 dark:hover:jF-bg-slate-800/80",
        ghost: "hover:jF-bg-slate-100 hover:jF-text-slate-900 dark:hover:jF-bg-slate-800 dark:hover:jF-text-slate-50",
        link: "jF-text-slate-900 jF-underline-offset-4 hover:jF-underline dark:jF-text-slate-50",
      },
      size: {
        default: "jF-h-10 jF-px-4 jF-py-2",
        sm: "jF-h-9 jF-rounded-md jF-px-3",
        lg: "jF-h-11 jF-rounded-md jF-px-8",
        icon: "jF-h-10 jF-w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
